<template>
        <div class="app-topbar">
            <!-- Esquerda: botão hamburguer (mobile) -->
            <div class="topbar-left">
                <button class="hamburger-btn lg:hidden" @click="toggleSidebar" aria-label="Menu">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="3" y1="6" x2="21" y2="6"/>
                        <line x1="3" y1="12" x2="21" y2="12"/>
                        <line x1="3" y1="18" x2="21" y2="18"/>
                    </svg>
                </button>
            </div>

            <!-- Direita: perfil do usuário -->
            <div class="topbar-right">
                <TenantSwitcher @pronto="$emit('tenant-pronto')" />

                <button
                    class="theme-toggle-btn"
                    @click="$emit('toggle-theme')"
                    :title="isDarkMode ? 'Mudar para Modo Claro' : 'Mudar para Modo Escuro'"
                >
                    <i :class="isDarkMode ? 'pi pi-sun' : 'pi pi-moon'"></i>
                </button>

                <button
                    v-if="usuario && usuario.id"
                    class="theme-toggle-btn notificacao-btn"
                    aria-label="Notificações"
                    title="Notificações"
                    @click="abrirNotificacoes"
                >
                    <i class="pi pi-bell"></i>
                    <Badge
                        v-if="notificacaoCount > 0"
                        :value="notificacaoCount > 99 ? '99+' : notificacaoCount"
                        severity="danger"
                        class="notificacao-badge"
                    />
                </button>

                <button
                    v-if="usuario && usuario.id"
                    class="user-pill"
                    @click="toggleMenu"
                    :title="usuario.nome || 'Administrador'"
                >
                    <div class="user-avatar">
                        {{ initials }}
                    </div>
                    <div class="user-info">
                        <span class="user-name">{{ usuario.nome || 'Administrador' }}</span>
                        <span class="user-role">{{ usuario.perfil || 'Administrador' }}</span>
                    </div>
                    <svg class="chevron-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="6 9 12 15 18 9"/>
                    </svg>
                </button>
            </div>

            <TieredMenu ref="menu" id="overlay_tmenu" :model="items" popup />

            <NotificacaoPanel
                v-model:visible="notificacaoPanelVisible"
                @count-changed="notificacaoCount = $event"
            />
        </div>
</template>

<script>
import TieredMenu from 'primevue/tieredmenu';
import Badge from 'primevue/badge';
import NotificacaoPanel from '@/components/NotificacaoPanel.vue';
import TenantSwitcher from '@/components/tenant/TenantSwitcher.vue';
import Notificacao from '@/service/notificacao.js';

export default {
    name: 'AppHeader',
    components: { TieredMenu, Badge, NotificacaoPanel, TenantSwitcher },
    props: {
        usuario: { type: Object, default: () => ({}) },
        isDarkMode: { type: Boolean, default: false }
    },
    emits: ['toggle-sidebar', 'toggle-theme', 'open-profile', 'open-password', 'logout', 'tenant-pronto'],
    data() {
        return {
            notificacaoCount: 0,
            notificacaoPanelVisible: false,
            notificacaoPollTimer: null,
            items: [
                { label: 'Perfil',  icon: 'pi pi-user',     command: () => this.$emit('open-profile') },
                { label: 'Senha',   icon: 'pi pi-lock',     command: () => this.$emit('open-password') },
                { separator: true },
                { label: 'Sair',    icon: 'pi pi-sign-out', command: () => this.$emit('logout') },
            ]
        };
    },
    computed: {
        initials() {
            const nome = this.usuario?.nome || 'A';
            return nome
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map(p => p[0].toUpperCase())
                .join('');
        }
    },
    watch: {
        'usuario.id'(value) {
            if (value) {
                this.fetchNotificacaoCount();
            } else {
                this.notificacaoCount = 0;
            }
        },
    },
    mounted() {
        this.fetchNotificacaoCount();
        this.notificacaoPollTimer = window.setInterval(() => {
            this.fetchNotificacaoCount();
        }, 60000);
        window.addEventListener('focus', this.fetchNotificacaoCount);
        window.addEventListener('notificacao-refresh', this.fetchNotificacaoCount);
    },
    beforeUnmount() {
        if (this.notificacaoPollTimer) {
            window.clearInterval(this.notificacaoPollTimer);
        }
        window.removeEventListener('focus', this.fetchNotificacaoCount);
        window.removeEventListener('notificacao-refresh', this.fetchNotificacaoCount);
    },
    methods: {
        toggleSidebar() {
            this.$emit('toggle-sidebar');
        },
        toggleMenu(event) {
            this.$refs.menu.toggle(event);
        },
        abrirNotificacoes() {
            this.fetchNotificacaoCount();
            this.notificacaoPanelVisible = true;
        },
        fetchNotificacaoCount() {
            if (!this.usuario?.id) {
                return;
            }

            Notificacao.count()
                .then((response) => {
                    this.notificacaoCount = Notificacao.parseCountResponse(response);
                })
                .catch(() => {});
        },
    }
}
</script>

<style scoped>
/* ── Topbar ─────────────────────────────────────────────────── */
.app-topbar {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px 0 16px;
    background: #1a1a1a;
    border-bottom: 1px solid #2a2a2a;
    position: sticky;
    top: 0;
    z-index: 100;
    flex-shrink: 0;
}

.topbar-left  { display: flex; align-items: center; }
.topbar-right { display: flex; align-items: center; }

/* ── Theme toggle ───────────────────────────────────────────── */
.theme-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    cursor: pointer;
    color: #8b949e;
    margin-right: 12px;
    transition: background 140ms ease, color 140ms ease, border-color 140ms ease;
}
.theme-toggle-btn:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #e6edf3;
    border-color: rgba(255, 255, 255, 0.25);
}
.theme-toggle-btn i {
    font-size: 16px;
}

.notificacao-btn {
    position: relative;
}

.notificacao-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 18px;
    height: 18px;
    line-height: 18px;
    font-size: 10px;
    pointer-events: none;
}

/* ── Hamburguer (mobile) ────────────────────────────────────── */
.hamburger-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    cursor: pointer;
    color: #8b949e;
    transition: background 140ms ease, color 140ms ease;
}
.hamburger-btn:hover { background: rgba(255,255,255,0.08); color: #e6edf3; }
.hamburger-btn svg   { width: 18px; height: 18px; }

/* ── User pill ──────────────────────────────────────────────── */
.user-pill {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 10px 5px 5px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 40px;
    cursor: pointer;
    transition: background 160ms ease, border-color 160ms ease;
    color: inherit;
}
.user-pill:hover {
    background: rgba(255, 255, 255, 0.09);
    border-color: rgba(255, 255, 255, 0.18);
}

/* Avatar com iniciais */
.user-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.04em;
    flex-shrink: 0;
    user-select: none;
}

/* Nome e papel */
.user-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.2;
}
.user-name {
    font-size: 13px;
    font-weight: 600;
    color: #e6edf3;
    white-space: nowrap;
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
}
.user-role {
    font-size: 11px;
    color: #6b7280;
    white-space: nowrap;
}

/* Chevron */
.chevron-icon {
    width: 14px;
    height: 14px;
    color: #6b7280;
    flex-shrink: 0;
    transition: transform 160ms ease;
}
.user-pill:hover .chevron-icon {
    transform: rotate(180deg);
    color: #8b949e;
}


</style>
